import { getOSSUploadSignature, getOSSUploadConfig } from '@/api';

/**
 * OSS直传工具类
 * 用于处理阿里云OSS直传功能，包括获取签名、配置验证、文件上传等
 */
class OSSUploader {
  constructor() {
    // 缓存签名信息，避免重复请求
    this.signature = null;
    this.config = null;
    this.signatureExpireTime = null;
  }

  /**
   * 获取OSS上传签名
   * @returns {Promise<Object>} 签名信息
   */
  async getSignature() {
    // 检查签名是否过期（提前5分钟刷新）
    if (this.signature && this.signatureExpireTime && Date.now() < this.signatureExpireTime - 5 * 60 * 1000) {
      return this.signature;
    }

    try {
      const response = await getOSSUploadSignature();
      if (response.data.code === 200) {
        this.signature = response.data.data;
        // 解析policy中的过期时间
        const policy = JSON.parse(atob(this.signature.policy));
        this.signatureExpireTime = new Date(policy.expiration).getTime();
        return this.signature;
      } else {
        throw new Error(response.data.message || '获取签名失败');
      }
    } catch (error) {
      console.error('获取OSS签名失败:', error);
      throw new Error('获取上传凭证失败，请检查网络连接');
    }
  }

  /**
   * 获取OSS上传配置
   * @returns {Promise<Object>} 配置信息
   */
  async getConfig() {
    if (this.config) {
      return this.config;
    }

    try {
      const response = await getOSSUploadConfig();
      if (response.data.code === 200) {
        console.log(response.data.data,"getOSSUploadConfig");
        this.config = response.data.data;
        return this.config;
      } else {
        throw new Error(response.data.message || '获取配置失败');
      }
    } catch (error) {
      console.error('获取OSS配置失败:', error);
      throw new Error('获取上传配置失败');
    }
  }
/**
 * 验证文件类型（兼容：jpg / .jpg 两种配置）
 * @param {File} file 文件对象
 * @param {Array} allowedTypes 允许的文件类型数组
 * @returns {boolean} 是否通过验证
 */
validateFileType(file, allowedTypes = []) {
  if (!Array.isArray(allowedTypes) || allowedTypes.length === 0) {
    return true;
  }

  // 1) 取文件扩展名（不带点）
  const fileName = (file?.name || "").toLowerCase();
  const fileExtension = fileName.includes(".") ? fileName.split(".").pop() : "";

  // 2) 规范化允许列表：去空格、转小写、去掉开头的点
  const normalizedAllowed = allowedTypes
    .filter(Boolean)
    .map(t => String(t).trim().toLowerCase())
    .map(t => (t.startsWith(".") ? t.slice(1) : t))
    .filter(Boolean);

  // 3) 比对
  return normalizedAllowed.includes(fileExtension);
}


  /**
   * 验证文件大小
   * @param {File} file 文件对象
   * @param {number} maxSize 最大文件大小（字节）
   * @returns {boolean} 是否通过验证
   */
  validateFileSize(file, maxSize) {
    console.log(file.size,maxSize,"validateFileSize");
    console.log(file.size <= maxSize,"validateFileSize");
    return file.size <= maxSize;
  }

  /**
   * 生成文件路径
   * @param {File} file 文件对象
   * @param {string} dir 目录路径
   * @param {boolean} useOriginalName 是否使用原文件名，默认为false
   * @returns {string} 文件路径
   */
  generateFilePath(file, dir = 'direct-upload', useOriginalName = false) {
    if (useOriginalName) {
      // 使用原文件名
      const originalFileName = file.name;
      return `${dir}/${originalFileName}`;
    }
    
    // 获取文件扩展名
    const extension = file.name.split('.').pop().toLowerCase();
    
    // 获取文件名（不含扩展名）
    const nameWithoutExt = file.name.substring(0, file.name.lastIndexOf('.'));
    
    // 生成时间戳（精确到毫秒）
    const timestamp = Date.now();
    
    // 生成随机字符串（8位）
    const randomStr = Math.random().toString(36).substring(2, 10);
    
    // 生成文件哈希（基于文件名和大小）
    const fileHash = this.generateFileHash(file.name, file.size);
    
    // 按照行业标准生成文件名：原文件名_时间戳_随机字符串_哈希.扩展名
    const fileName = `${nameWithoutExt}_${timestamp}_${randomStr}_${fileHash}.${extension}`;
    
    return `${dir}/${fileName}`;
  }

  /**
   * 生成文件哈希
   * @param {string} fileName 文件名
   * @param {number} fileSize 文件大小
   * @returns {string} 文件哈希
   */
  generateFileHash(fileName, fileSize) {
    // 简单的哈希算法，基于文件名和大小
    let hash = 0;
    const str = fileName + fileSize.toString();
    
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // 转换为32位整数
    }
    
    // 转换为16进制并取前8位
    return Math.abs(hash).toString(16).substring(0, 8);
  }

  /**
   * 格式化文件大小
   * @param {number} bytes 字节数
   * @returns {string} 格式化后的文件大小
   */
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * 上传文件到OSS
   * @param {File} file 文件对象
   * @param {Function} onProgress 进度回调函数
   * @param {string} customDir 自定义目录
   * @param {boolean} useOriginalName 是否使用原文件名
   * @returns {Promise<Object>} 上传结果
   */
  async uploadFile(file, onProgress = null, customDir = null, useOriginalName = false) {
    try {
      // 获取配置和签名
      const [config, signature] = await Promise.all([
        this.getConfig(),
        this.getSignature()
      ]);

      // 验证文件类型
      if (!this.validateFileType(file, config.allowedFileTypes)) {
        throw new Error(`不支持的文件类型，支持的类型：${config.allowedFileTypes.join(', ')}`);
      }

      // 验证文件大小
      if (!this.validateFileSize(file, config.maxFileSize)) {
        throw new Error(`文件大小超出限制，最大允许：${this.formatFileSize(config.maxFileSize)}`);
      }

      // 生成文件路径
      const dir = customDir || signature.dir;
      console.log(dir,"dir");
      const filePath = this.generateFilePath(file, dir, useOriginalName);

      // 构建FormData - 按照阿里云官方文档要求，共10个参数
      const formData = new FormData();
      formData.append('success_action_status', '200');
      formData.append('policy', signature.policy);
      formData.append('x-oss-signature', signature.signature);
      formData.append('x-oss-signature-version', signature.version);
      formData.append('x-oss-credential', signature.x_oss_credential);
      formData.append('x-oss-date', signature.x_oss_date);
      formData.append('key', filePath);
      formData.append('x-oss-security-token', signature.security_token);
    //   formData.append('callback', signature.callback);
      formData.append('file', file); // file 必须为最后一个表单域

      // 上传文件
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        // 监听上传进度
        if (onProgress) {
          xhr.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable) {
              const percentCompleted = Math.round((event.loaded * 100) / event.total);
              onProgress(percentCompleted);
            }
          });
        }

        // 监听请求完成
        xhr.addEventListener('load', () => {
          if (xhr.status === 200) {
            try {
              // 尝试解析响应
              const response = JSON.parse(xhr.responseText);
              resolve({
                success: true,
                url: `${signature.host}/${filePath}`,
                path: filePath,
                fileName: file.name,
                fileSize: file.size,
                response: response
              });
            } catch (error) {
              // 如果解析失败，说明是直接上传成功
              resolve({
                success: true,
                url: `${signature.host}/${filePath}`,
                path: filePath,
                fileName: file.name,
                fileSize: file.size
              });
            }
          } else {
            reject(new Error(`上传失败: ${xhr.status} ${xhr.statusText}`));
          }
        });

        // 监听请求错误
        xhr.addEventListener('error', () => {
          reject(new Error('网络错误，请检查网络连接'));
        });

        // 监听请求超时
        xhr.addEventListener('timeout', () => {
          reject(new Error('上传超时，请检查网络连接或尝试上传较小的文件'));
        });

        // 设置超时时间（5分钟）
        xhr.timeout = 300000;

        // 发送请求到OSS
        xhr.open('POST', signature.host);
        xhr.send(formData);
      });

    } catch (error) {
      console.error('文件上传失败:', error);
      throw error;
    }
  }

  /**
   * 批量上传文件
   * @param {Array<File>} files 文件数组
   * @param {Function} onProgress 总体进度回调
   * @param {Function} onFileProgress 单个文件进度回调
   * @param {string} customDir 自定义目录
   * @param {boolean} useOriginalName 是否使用原文件名
   * @returns {Promise<Array>} 上传结果数组
   */
  async uploadFiles(files, onProgress = null, onFileProgress = null, customDir = null, useOriginalName = false) {
    const results = [];
    let completedFiles = 0;

    for (let i = 0; i < files.length; i++) {
      try {
        const file = files[i];
        const result = await this.uploadFile(file, (progress) => {
          if (onFileProgress) {
            onFileProgress(progress, i);
          }
        }, customDir, useOriginalName);

        results.push(result);
        completedFiles++;

        // 更新总体进度
        if (onProgress) {
          const totalProgress = Math.round((completedFiles * 100) / files.length);
          onProgress(totalProgress);
        }

      } catch (error) {
        results.push({
          success: false,
          fileName: files[i].name,
          error: error.message
        });
        completedFiles++;

        // 更新总体进度
        if (onProgress) {
          const totalProgress = Math.round((completedFiles * 100) / files.length);
          onProgress(totalProgress);
        }
      }
    }

    return results;
  }

  /**
   * 清除缓存的签名和配置
   */
  clearCache() {
    this.signature = null;
    this.config = null;
    this.signatureExpireTime = null;
  }
}

// 创建单例实例
const ossUploader = new OSSUploader();

export default ossUploader; 